import { Row, Col } from 'reactstrap';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import { Registration, RegistrationTotals } from '~/types/campaign';
import moment from 'moment';

const RegistrationsTable = ({
  registrations,
  registrationTotals,
  maxRegistrants,
}: {
  registrations: Registration[];
  registrationTotals: RegistrationTotals;
  maxRegistrants?: number | null;
}) => {
  if (!registrations || !registrationTotals) {
    return <div />;
  }
  const { item_totals, additional_campaign_amounts } = registrationTotals;

  const sorted = [...registrations].sort(
    (a, b) =>
      new Date(b.created_date).getTime() - new Date(a.created_date).getTime()
  );

  const grandTotal =
    registrations.reduce((acc, item) => {
      if (item.canceled) return acc;
      return acc + parseFloat(item.total);
    }, 0) + additional_campaign_amounts;

  const attendeesFor = (registration: Registration) =>
    registration.registration_details.reduce((acc, detail) => {
      if (detail.registration_addon) return acc;
      if (detail.registration_option) {
        const perItem =
          detail.registration_option.attendees_per_item ?? 1;
        return acc + detail.quantity * perItem;
      }
      // legacy v1 detail (or detail with neither option nor addon)
      return acc + detail.quantity;
    }, 0);

  const totalAttendees = registrations.reduce(
    (acc, r) => (r.canceled ? acc : acc + attendeesFor(r)),
    0
  );
  return (
    <>
      <Table className='table min-w-full bg-white'>
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Date</Th>
            <Th>Items</Th>
            <Th className='text-end'>Attendees</Th>
            <Th className='text-end'>Additional Donation</Th>
            {/* <Th className='text-end'>Total</Th> */}
            <Th className='w-25'>Comment</Th>
          </Tr>
        </Thead>

        <Tbody>
          {sorted.map((registration, index) => {
            const name = `${registration.first_name} ${registration.last_name}`;
            const total =
              parseFloat(registration.total) +
              parseFloat(registration.additional_campaign_amount);
            return (
              <Tr key={index} className={registration.canceled ? 'opacity-50' : ''}>
                <Td className='fullwidth'>
                  <div className='fw-bold d-flex align-items-center gap-2'>
                    <span>{name}</span>
                    {registration.canceled && (
                      <span className='badge bg-secondary'>Canceled</span>
                    )}
                  </div>
                  {registration.canceled && registration.canceled_at && (
                    <div className='small text-muted'>
                      refunded {moment(registration.canceled_at).format('L')}
                    </div>
                  )}
                  <div className='small'>
                    <div>
                      <a href={`mailto:${registration.email}`}>
                        {registration.email}
                      </a>
                    </div>
                    <div>
                      <a href={`tel:${registration.phone}`}>
                        {registration.phone}
                      </a>
                    </div>
                    <div>
                      {registration.address_1}, {registration.city},{' '}
                      {registration.state} {registration.zip_code}
                    </div>
                  </div>
                </Td>

                <Td>{moment(registration.created_date).format('L')}</Td>
                <Td className='fullwidth'>
                  {(() => {
                    if (registration.canceled) {
                      return <span className='text-muted'>&mdash;</span>;
                    }
                    const options = registration.registration_details.filter(
                      (d) => d.registration_option
                    );
                    const addons = registration.registration_details.filter(
                      (d) => d.registration_addon
                    );
                    const legacyItems = registration.registration_details.filter(
                      (d) => !d.registration_option && !d.registration_addon
                    );
                    const hasV2 = options.length > 0 || addons.length > 0;

                    if (!hasV2) {
                      // Legacy: flat list with (add-on) label
                      return registration.registration_details.map(
                        (detail, index) => {
                          const source = detail.item;
                          const name = source?.name ?? '';
                          const unitPrice = detail.price_paid
                            ? parseFloat(detail.price_paid)
                            : source?.price ?? 0;
                          return (
                            <div
                              key={index}
                              className='d-flex gap-2 justify-content-between'
                            >
                              <div className='w-50'>{name}</div>
                              <div>x{detail.quantity}</div>
                              <div className='w-25'>
                                ${(unitPrice * detail.quantity).toFixed(2)}
                              </div>
                            </div>
                          );
                        }
                      );
                    }

                    return (
                      <>
                        {options.length > 0 && (
                          <div>
                            <div className='fw-bold small mb-1'>
                              Registration Options
                            </div>
                            {(() => {
                              // Group options by option id + answer
                              const grouped: {
                                key: string;
                                name: string;
                                unitPrice: number;
                                quantity: number;
                                answer: string;
                              }[] = [];
                              options.forEach((detail) => {
                                const name =
                                  detail.registration_option?.name ?? '';
                                const unitPrice = detail.price_paid
                                  ? parseFloat(detail.price_paid)
                                  : detail.registration_option?.price ?? 0;
                                const answer =
                                  detail.option_question_answer || '';
                                const key = `${detail.registration_option?.id}-${answer}`;
                                const existing = grouped.find(
                                  (g) => g.key === key
                                );
                                if (existing) {
                                  existing.quantity += detail.quantity;
                                } else {
                                  grouped.push({
                                    key,
                                    name,
                                    unitPrice,
                                    quantity: detail.quantity,
                                    answer,
                                  });
                                }
                              });
                              return grouped.map((group) => (
                                <div key={group.key}>
                                  <div className='d-flex gap-2 justify-content-between'>
                                    <div className='w-50'>{group.name}</div>
                                    <div>x{group.quantity}</div>
                                    <div className='w-25'>
                                      $
                                      {(
                                        group.unitPrice * group.quantity
                                      ).toFixed(2)}
                                    </div>
                                  </div>
                                  {group.answer && (
                                    <div className='rounded-1 py-1 px-2 bg-lighter small mt-1 mb-2'>
                                      {group.answer}
                                    </div>
                                  )}
                                </div>
                              ));
                            })()}
                          </div>
                        )}
                        {addons.length > 0 && (
                          <div
                            className={`${
                              options.length > 0
                                ? 'ms-3 mt-2 ps-2 border-start'
                                : ''
                            }`}
                          >
                            <div className='fw-bold small mb-1'>
                              Optional Add-ons
                            </div>
                            {(() => {
                              // Group add-ons by addon id + selection
                              const grouped: {
                                key: string;
                                name: string;
                                unitPrice: number;
                                quantity: number;
                                selection: string;
                              }[] = [];
                              addons.forEach((detail) => {
                                const name =
                                  detail.registration_addon?.name ?? '';
                                const unitPrice = detail.price_paid
                                  ? parseFloat(detail.price_paid)
                                  : detail.registration_addon?.price ?? 0;
                                const selection =
                                  detail.addon_option_selection || '';
                                const key = `${detail.registration_addon?.id}-${selection}`;
                                const existing = grouped.find(
                                  (g) => g.key === key
                                );
                                if (existing) {
                                  existing.quantity += detail.quantity;
                                } else {
                                  grouped.push({
                                    key,
                                    name,
                                    unitPrice,
                                    quantity: detail.quantity,
                                    selection,
                                  });
                                }
                              });
                              return grouped.map((group) => (
                                <div key={group.key}>
                                  <div className='d-flex gap-2 justify-content-between'>
                                    <div className='w-50'>{group.name}</div>
                                    <div>x{group.quantity}</div>
                                    <div className='w-25'>
                                      $
                                      {(
                                        group.unitPrice * group.quantity
                                      ).toFixed(2)}
                                    </div>
                                  </div>
                                  {group.selection && (
                                    <div className='rounded-1 py-1 px-2 bg-lighter small mt-1 mb-2'>
                                      {group.selection}
                                    </div>
                                  )}
                                </div>
                              ));
                            })()}
                          </div>
                        )}
                        {legacyItems.map((detail, index) => {
                          const source = detail.item;
                          const name = source?.name ?? '';
                          const unitPrice = detail.price_paid
                            ? parseFloat(detail.price_paid)
                            : source?.price ?? 0;
                          return (
                            <div
                              key={`legacy-${index}`}
                              className='d-flex gap-2 justify-content-between'
                            >
                              <div className='w-50'>{name}</div>
                              <div>x{detail.quantity}</div>
                              <div className='w-25'>
                                ${(unitPrice * detail.quantity).toFixed(2)}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </Td>
                <Td className='fullwidth text-end'>
                  {attendeesFor(registration)}
                </Td>
                <Td className='fullwidth text-end'>
                  {registration.additional_campaign_amount !== '0.00' && (
                    <>${registration.additional_campaign_amount}</>
                  )}
                </Td>
                {/* <Td className='text-end'>${total.toFixed(2)}</Td> */}
                <Td className='fullwidth'>
                  {registration.registration_answer && (
                    <div className='rounded-1 py-1 px-2 bg-lighter small'>
                      {registration.registration_answer}
                    </div>
                  )}
                  {registration.message_to_organizer && (
                    <div className='rounded-1 py-1 px-2 bg-lighter small'>
                      {registration.message_to_organizer}
                    </div>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <Row className='justify-content-end'>
        <Col lg={9} xl={7} className='px-3 py-2 rounded-1 bg-lighter'>
          <Row className='py-3 border-bottom border-dark'>
            <Col sm={3} className='fw-bold'>
              Total Items
            </Col>
            <Col>
              {item_totals.map((item, index) => {
                return (
                  <div key={index} className='d-flex justify-content-between'>
                    <div>{item.item__name}</div>
                    <div>x{item.quantity}</div>
                  </div>
                );
              })}
            </Col>
            <Col sm={2} className='text-end fw-bold'>
              {item_totals.map((item, index) => {
                return <div key={index}>${item.total}.00</div>;
              })}
            </Col>
          </Row>
          <Row className='justify-content-between py-3 border-bottom border-dark'>
            <Col className='fw-bold'>Total Attendees</Col>
            <Col className='text-end fw-bold'>
              <div>
                {totalAttendees}
                {maxRegistrants ? (
                  <>
                    {' / '}
                    {maxRegistrants}
                    {totalAttendees >= maxRegistrants && (
                      <span className='badge bg-danger ms-2'>Full</span>
                    )}
                  </>
                ) : null}
              </div>
            </Col>
          </Row>
          <Row className='justify-content-between py-3 border-bottom border-dark'>
            <Col className='fw-bold'>Additional Campaign Donations</Col>
            <Col className='text-end fw-bold'>
              <div>${additional_campaign_amounts}</div>
            </Col>
          </Row>
          <Row className='justify-content-between py-3'>
            <Col className='fw-bold'>Grand Total</Col>
            <Col className='text-end fw-bold'>
              <div>${grandTotal.toFixed(2)}</div>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default RegistrationsTable;
